import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import IconWrapper from '@components/ui/Icons';
import { useUI } from '@context/GlobalUIContext';
import { useStoresUI } from '@context/StoresContext';
import { useState, useEffect } from 'react';
import {
  geoLocationPrompt,
  checkGeoLocationPermissions,
} from './utils/geolocationUtils';
import { trackStoreInteraction } from '@utils/gtm/ga4/trackStoreInteraction';

import styles from './GeoLocationButton.module.scss';

export interface GeoLocationButtonProps {
  classNames?: string | string[];
  /** When provided (e.g. from app router with next-intl), used for button label instead of next-i18next */
  label?: string;
}

const GeoLocationButton = ({
  classNames,
  label: labelProp,
}: GeoLocationButtonProps) => {
  const { setLocationAndFavouriteStore } = useStoresUI();
  const { closeStoresModal } = useUI();
  const [isGeoLocationAllowed, setIsGeoLocationAllowed] = useState(false);
  const [isGeoLocationRunning, setIsGeoLocationRunning] = useState(false);
  const { t } = useTranslation('common');
  const label = labelProp ?? t('modal.stores.useLocation');

  const onGeoLocationClick = (text: string) => {
    trackStoreInteraction({
      data: {
        linkUrl: null,
        linkText: text,
      },
    });
    const onAllowed = (result: any) => {
      if (result.coords.latitude && result.coords.longitude) {
        const location = {
          latitude: result.coords.latitude,
          longitude: result.coords.longitude,
        };
        setLocationAndFavouriteStore(location);
        setIsGeoLocationRunning(false);
        closeStoresModal();
      }
    };
    const onDenied = () => {
      setIsGeoLocationRunning(false);
    };
    setIsGeoLocationRunning(true);
    geoLocationPrompt(onAllowed, onDenied, window.navigator);
  };

  useEffect(() => {
    const onLocationApproved = () => setIsGeoLocationAllowed(true);
    const onLocationDenied = () => setIsGeoLocationAllowed(false);
    checkGeoLocationPermissions(onLocationApproved, onLocationDenied);
  }, []);

  return isGeoLocationAllowed ? (
    <button
      type="button"
      className={cn(styles.useLocationButton, classNames)}
      onClick={() => onGeoLocationClick(label)}
    >
      {isGeoLocationRunning && (
        <LoadingSpinner
          classNames={styles.loadingSpinner}
          colour="var(--color-base-font)"
        />
      )}
      <IconWrapper
        icon="geo-disabled"
        width={18}
        height={18}
        classNames={styles.icon}
        fill="var(--color-base-font)"
      />
      {label}
    </button>
  ) : null;
};

export default GeoLocationButton;
